import { useCallback } from 'react';

import { GridColumnResizeParams } from '@mui/x-data-grid-pro';

import { useLocalStorage } from '@/hooks/useLocalStorage';

/**
 * Column widths stored as a map of field -> width in pixels
 */
export type ColumnWidths = Record<string, number>;

/**
 * Generic hook for managing DataGrid column widths with localStorage persistence
 *
 * @param storageKey - Unique key for localStorage (e.g., 'portfolio-table-widths')
 * @param defaultWidths - Optional default column widths configuration
 * @returns Column widths state and handlers
 */
export const useColumnWidths = (
  storageKey: string,
  defaultWidths: ColumnWidths = {},
) => {
  const [columnWidths, setColumnWidths] = useLocalStorage<ColumnWidths>(
    storageKey,
    defaultWidths,
  );

  // Handle column width change from DataGrid
  const handleColumnWidthChange = useCallback(
    (params: GridColumnResizeParams) => {
      // Only save if field is present and width is a valid number
      if (params.colDef.field && typeof params.width === 'number') {
        setColumnWidths((prev) => ({
          ...prev,
          [params.colDef.field]: params.width,
        }));
      }
    },
    [setColumnWidths],
  );

  return {
    columnWidths,
    handleColumnWidthChange,
    setColumnWidths,
  };
};
